import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import AssetType from '../../../components/Inputs/AssetType';
import { actualValueForDropdown } from '../../../utils/common';
import Input from '../../../common/Form/Input';
import { debounce } from 'lodash';
import { motion } from 'framer-motion';
import ButtonScogoOrange from '../../../common/Buttons/ButtonScogoOrange';
import scogo_apis from '../../../scogo_apis';
import { useDispatch } from 'react-redux';
import { openCustomModal } from '../../../actions/modal';
import AddAsset from '../Modal/AddAsset';
import { useSelector } from 'react-redux';
export const boqFormName = 'boq';

const Boq = ({ setShowBoq, className = 'bg-gray-100' }) => {
    const dispatch = useDispatch();
    const { watch, control, setValue, formState } = useFormContext();
    const { fields, append, remove } = useFieldArray({
        control,
        name: boqFormName
    });

    const selectedProject = watch('project');
    const orgId = useMemo(() => actualValueForDropdown(selectedProject?.org_id), [selectedProject]);

    useEffect(() => {
        if (fields.length === 0) {
            append({ asset_type_id: null });
        }
    }, [fields.length, append]);

    const debouncedUpdate = useCallback(
        debounce((assetType, values, index) => {
            if (!assetType?.fields) return;

            const boqPayload = {
                asset_type_id: actualValueForDropdown(assetType),
                asset_id: values[`${boqFormName}[${index}].asset_id`] || null
            };

            assetType.fields.forEach(field => {
                boqPayload[field.key] = values[`${boqFormName}[${index}].${field.key}`] || '';
            });

        }, 300),
        [orgId]
    );

    useEffect(() => {
        const subscription = watch((values) => {
            fields.forEach((field, index) => {
                const assetType = values[`${boqFormName}[${index}].asset_type_id`];
                if (assetType?.fields) {
                    debouncedUpdate(assetType, values, index);
                }
            });
        });

        return () => subscription.unsubscribe();
    }, [watch, debouncedUpdate, fields]);

    const PlusIcon = () => {
        return <span className='material-icons-outlined text-font11'>add</span>;
    };

    const openAddAssetModal = ({ serial_number, asset_type_id, key, location_id }) => {
        const props = {
            serial_number, org_id: orgId, additionalProps: { source: 'create_ticket_form' }, key, asset_type_id: actualValueForDropdown(asset_type_id), location_id: actualValueForDropdown(location_id),
            onSuccess: (response) => {
                setValue(key, response.serial_number, { shouldValidate: true });
            }
        };
        dispatch(
            openCustomModal({
                heading: 'Add Asset',
                modalWidth: '95rem',
                modalHeight: 'auto',
                modalComponent: <AddAsset {...props} />,
            })
        );
    };

    return (
        <motion.div
            className={`rounded-lg px-6 py-3 transition-transform transform hover:scale-105 ${className}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <div className='flex justify-between items-center border-b-2 border-scogoprimary pb-2'>
                <p className='text-lg font-semibold text-scogoprimary pb-2'>BOQ</p>
                <button
                    type="button"
                    onClick={() => {
                        setShowBoq(false);
                        setValue(boqFormName, null);
                    }}
                    className="text-gray-500 hover:text-gray-700"
                >
                    <span className="material-icons-outlined" style={{ fontSize: '16px' }}>close</span>
                </button>
            </div>

            {fields.map((field, index) => {
                const prefix = `${boqFormName}[${index}]`;
                const assetType = watch(`${prefix}.asset_type_id`);

                return (
                    <motion.div
                        key={field.id}
                        className="mb-6 pb-4 border-b border-gray-200"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-md font-medium">Asset {index + 1}</h3>
                            {fields.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => remove(index)}
                                    className="text-red-500 hover:text-red-700"
                                >
                                    <span className="material-icons-outlined" style={{ fontSize: '13px' }}>delete</span>
                                </button>
                            )}
                        </div>

                        <motion.div className='grid grid-cols-1 md:grid-cols-5 gap-4'>
                            <motion.div transition={{ delay: 0.1 }}>
                                <AssetType
                                    addNew
                                    label='Asset Type'
                                    name={`${prefix}.asset_type_id`}
                                    org_id={orgId}
                                    className='w-full'
                                />
                            </motion.div>
                            {assetType?.fields?.map((fieldItem) => {
                                if (fieldItem.key === 'serial_number') {
                                    return <motion.div key={fieldItem.key} transition={{ delay: 0.1 }}>
                                        <Input
                                            required={fieldItem.is_required}
                                            name={`${prefix}.${fieldItem.key}`}
                                            label={fieldItem.label}
                                            className='w-full'
                                            validate={{
                                                serialNumberNotInSystem: (value) => {
                                                    // Skip validation if empty or during form submission
                                                    if (value === '' || formState?.isSubmitting) return true;

                                                    // Only validate on change, not on submit
                                                    if (formState?.isSubmitting) return true;

                                                    return new Promise(resolve => {
                                                        const validateSerialNumber = async () => {
                                                            try {
                                                                const response = await scogo_apis.inventory.checkAsset({}, {}, {}, { serial_number: value, org_id: orgId });
                                                                const selectedAssetType = watch(`${prefix}.asset_type_id`);
                                                                if (response.status === 201 && response.data.data.asset_type_id !== selectedAssetType?.value) {
                                                                    if (response?.data?.data?.org_id === orgId) {
                                                                        setValue(`${prefix}.asset_id`, response.data.data.id);
                                                                        setValue(`${prefix}.make`, response.data.data.make);
                                                                        setValue(`${prefix}.model`, response.data.data.model);
                                                                        setValue(`${prefix}.partcode`, response.data.data.partcode);
                                                                        setValue(`${prefix}.validating`, false);
                                                                        setValue(`${prefix}.asset_not_found`, false);

                                                                        if (response.data.data.asset_type.fields && response.data.data.asset_type.fields.length > 0) {
                                                                            setValue(`${prefix}.asset_type_id`, {
                                                                                key: response.data.data.asset_type.id,
                                                                                label: response.data.data.asset_type.title,
                                                                                fields: response.data.data.asset_type.fields
                                                                            });
                                                                        } else {
                                                                            try {
                                                                                const assetTypeResponse = await scogo_apis.inventory.getAssetTypeList({}, {}, { org_id: orgId, q: response.data.data.asset_type.title });
                                                                                if (assetTypeResponse.status === 200) {
                                                                                    const assetTypeWithFields = assetTypeResponse.data.find(type => type.id === response.data.data.asset_type.id);
                                                                                    if (assetTypeWithFields) {
                                                                                        setValue(`${prefix}.asset_type_id`, {
                                                                                            key: assetTypeWithFields.id,
                                                                                            label: assetTypeWithFields.title,
                                                                                            fields: assetTypeWithFields.additional_fields || []
                                                                                        });
                                                                                    } else {
                                                                                        setValue(`${prefix}.asset_type_id`, {
                                                                                            key: response.data.data.asset_type.id,
                                                                                            label: response.data.data.asset_type.title,
                                                                                            fields: []
                                                                                        });
                                                                                    }
                                                                                } else {
                                                                                    setValue(`${prefix}.asset_type_id`, {
                                                                                        key: response.data.data.asset_type.id,
                                                                                        label: response.data.data.asset_type.title,
                                                                                        fields: []
                                                                                    });
                                                                                }
                                                                            } catch (assetTypeError) {
                                                                                console.error("Error fetching asset type details:", assetTypeError);
                                                                                setValue(`${prefix}.asset_type_id`, {
                                                                                    key: response.data.data.asset_type.id,
                                                                                    label: response.data.data.asset_type.title,
                                                                                    fields: []
                                                                                });
                                                                            }
                                                                        }
                                                                        resolve(true);
                                                                        return;
                                                                    }
                                                                    setValue(`${prefix}.asset_id`, null);
                                                                    setValue(`${prefix}.make`, null);
                                                                    setValue(`${prefix}.model`, null);
                                                                    setValue(`${prefix}.partcode`, null);
                                                                    setValue(`${prefix}.validating`, false);
                                                                    setValue(`${prefix}.asset_not_found`, true);
                                                                    resolve("Asset doesn't belong to your organization.");
                                                                    return;
                                                                }
                                                                if (response.status === 201 && response.data.data.id) {
                                                                    setValue(`${prefix}.asset_id`, response.data.data.id);
                                                                    setValue(`${prefix}.make`, response.data.data.make);
                                                                    setValue(`${prefix}.model`, response.data.data.model);
                                                                    setValue(`${prefix}.partcode`, response.data.data.partcode);
                                                                    setValue(`location_id`, {
                                                                        label: response.data.data.location.external_id,
                                                                        value: response.data.data.location.id,
                                                                        key: response.data.data.location.id
                                                                    });
                                                                    setValue(`${prefix}.validating`, false);
                                                                    setValue(`${prefix}.asset_not_found`, false);
                                                                    resolve(true);
                                                                } else {
                                                                    setValue(`${prefix}.validating`, false);
                                                                    setValue(`${prefix}.asset_not_found`, true);
                                                                    resolve("Serial Number doesn't exist in the System.");
                                                                }
                                                            } catch (error) {
                                                                console.error("Error checking serial number:", error);
                                                                setValue(`${prefix}.validating`, false);
                                                                setValue(`${prefix}.asset_not_found`, true);
                                                                resolve(error?.response?.data?.messages?.[0] || 'Error checking serial number');
                                                            }
                                                        };

                                                        try {
                                                            setValue(`${prefix}.validating`, true);
                                                            setValue(`${prefix}.asset_not_found`, false);
                                                            if (!window.debouncedSerialValidation) {
                                                                window.debouncedSerialValidation = debounce((callback) => {
                                                                    callback();
                                                                });
                                                            }
                                                            window.debouncedSerialValidation(validateSerialNumber);
                                                        } catch (error) {
                                                            console.error("Error in validation:", error);
                                                            setValue(`${prefix}.validating`, false);
                                                            setValue(`${prefix}.asset_not_found`, false);
                                                            resolve(error?.response?.data?.messages?.[0] || 'Error validating serial number. Please try again.');
                                                        }
                                                    });
                                                },
                                            }}
                                            isLoading={watch(`${prefix}.validating`)}
                                            iconRight={watch(`${prefix}.asset_not_found`) ? { name: 'add', hoverTitle: 'Add Asset', onClick: () => openAddAssetModal({ serial_number: watch(`${prefix}.serial_number`), asset_type_id: watch(`${prefix}.asset_type_id`), key: `${prefix}.serial_number`, location_id: watch(`location_id`) }) } : null}
                                        />
                                    </motion.div>
                                }
                                return <motion.div key={fieldItem.key} transition={{ delay: 0.1 }}>
                                    <Input
                                        required={fieldItem.is_required}
                                        name={`${prefix}.${fieldItem.key}`}
                                        label={fieldItem.label}
                                        className='w-full'
                                    />
                                </motion.div>
                            })}
                        </motion.div>
                    </motion.div>
                );
            })}

            <ButtonScogoOrange
                buttonIconComponent={<PlusIcon />}
                textOrComponent={'Add Asset'}
                onClick={() => append({ asset_type_id: null })}
                buttonClass='my-2 py-1'
            />
        </motion.div>
    );
};

export default Boq;
